import { RotateCcw, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import type { ChangeKind, RestoreScope } from "../../api/types";

// 復原的二次確認。
//
// 確認文字一定要講清楚**兩件事**:
// 1. 會對現在的內容做什麼 —— 使用者按之前得知道自己要付出什麼。
// 2. 復原本身也會產生一個新版本,現在的內容不會消失 ——
//    少了這一句,使用者會以為按下去就再也回不來,於是根本不敢按,
//    這個功能等於白做。
//
// 兩句話都由這裡組出來,不散在各個按鈕旁邊:散開來寫,遲早有一種粒度漏講一半。
//
// 第一句要分**變更種類**,不能只分粒度。差異清單把「新增」與「刪除」畫成
// 同樣一顆「復原」按鈕,但兩者的後果恰恰相反:
//   - 被標成「新增」的分類/明細,舊版根本沒有它 —— 復原等於**整個刪掉**。
//   - 被標成「刪除」的,現在根本不存在 —— 復原等於**整個加回來**。
// 一律說「內容會被蓋掉」的話,使用者按下確定、那個分類連同底下每一列一起
// 消失,而他從頭到尾沒被告知這是刪除。

export interface RestoreTarget {
  scope: RestoreScope;
  /** 分類或明細的 uid;整張單是 null。 */
  uid: string | null;
  /** 分類或明細的名稱,整張單留空字串。 */
  name: string;
  /**
   * 這個分類/這一列在差異清單上被標成什麼。復原的實際後果完全取決於它。
   * 整張單沒有「新增/刪除」可言,固定給 `CHANGED`。
   */
  change: ChangeKind;
  /** 分類底下有幾列明細。刪掉整個分類時要講清楚會一起帶走幾列。 */
  lineCount?: number;
}

/** 「(含 3 列明細)」。沒有明細就不加這一段,免得寫出「含 0 列明細」。 */
function withLines(target: RestoreTarget): string {
  if (target.scope !== "SECTION" || !target.lineCount) return "";
  return `(含 ${target.lineCount} 列明細)`;
}

/** 復原之後這個分類/這一列會整個不見嗎? */
export function isDestructive(target: RestoreTarget): boolean {
  return target.scope !== "QUOTE" && target.change === "ADDED";
}

/** 這一次復原會對現在的內容做什麼、影響範圍到哪裡。 */
function overwriteSentence(target: RestoreTarget, fromVersion: number): string {
  const isSection = target.scope === "SECTION";
  const what = isSection ? `分類「${target.name}」` : `明細「${target.name}」`;
  const others = isSection ? "同一張單的其他分類不受影響。" : "同一個分類的其他明細不受影響。";

  if (target.scope === "QUOTE") {
    return `整張單現在的內容會被版本 ${fromVersion} 的內容蓋掉,所有分類與明細都會回到版本 ${fromVersion} 的樣子。`;
  }
  if (target.change === "ADDED") {
    const how = isSection ? `整個刪掉${withLines(target)}` : "整列刪掉";
    return `版本 ${fromVersion} 沒有${what},復原等於把它${how},${others}`;
  }
  if (target.change === "REMOVED") {
    const how = isSection ? `整個加回來${withLines(target)}` : "整列加回來";
    return `${what}目前已經不在了,復原會把它${how},現有的內容不受影響。`;
  }
  return `${what}現在的內容會被版本 ${fromVersion} 的內容蓋掉,${others}`;
}

/** 這一句一個字都不能少,詳見本檔頂端的說明。 */
const KEEPS_HISTORY = "復原本身也會產生一個新版本,現在的內容不會消失。復原錯了,再從版本紀錄復原回來就好。";

export interface RestoreConfirmDialogProps {
  target: RestoreTarget | null;
  /** 要復原成哪一版。 */
  fromVersion: number | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RestoreConfirmDialog({
  target,
  fromVersion,
  pending,
  error,
  onCancel,
  onConfirm,
}: RestoreConfirmDialogProps) {
  const open = target !== null && fromVersion !== null;
  // 這一次復原會讓東西整個消失時,確認鈕改成危險樣式並換成垃圾桶圖示 ——
  // 樣式與圖示是提醒,真正說清楚後果的仍然是上面那句話,不是只靠顏色。
  const destructive = target !== null && isDestructive(target);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="復原確認"
      // 二次確認點背景不關:手滑關掉之後,使用者搞不清楚到底復原了沒。
      closeOnBackdrop={false}
      footer={
        <>
          <Button onClick={onCancel}>取消</Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            icon={
              destructive ? (
                <Trash2 aria-hidden="true" size={16} />
              ) : (
                <RotateCcw aria-hidden="true" size={16} />
              )
            }
            loading={pending}
            onClick={onConfirm}
          >
            確定復原
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="m-0 mb-3 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}

      {open ? (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-[var(--text)]">{overwriteSentence(target, fromVersion)}</p>
          <p className="m-0 text-sm text-[var(--text-2)]">{KEEPS_HISTORY}</p>
        </div>
      ) : null}
    </Dialog>
  );
}
